import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import UsageBar from "@/components/UsageBar";
import { useAuth } from "@/lib/auth";
import { CheckCircle, CreditCard } from "@phosphor-icons/react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/billing/orders").then((r) => setOrders(r.data));
    api.get("/usage").then((r) => setUsage(r.data));
    refresh?.();
    // eslint-disable-next-line
  }, []);

  return (
    <DashboardLayout title="Settings">
      <div className="grid grid-cols-12 gap-4">
        {usage && (
          <div className="col-span-12">
            <UsageBar usage={usage} />
          </div>
        )}
        <div className="col-span-12 lg:col-span-7 cell-strong bg-white p-6">
          <div className="overline text-[var(--ink-soft)]">/ Account</div>
          <div className="font-display text-2xl mt-1 mb-4">Workspace</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Role" value={user?.role} />
            <Row label="Plan" value={user?.plan ? user.plan.toUpperCase() : "TRIAL"} />
            <Row label="Created" value={user?.created_at ? new Date(user.created_at).toLocaleString() : "—"} />
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 cell-strong bg-[var(--ink)] text-white p-6">
          <div className="overline text-white/60">/ Heads up</div>
          <div className="font-display text-2xl mt-1">Real Stripe is coming</div>
          <p className="text-white/70 mt-2 text-sm">
            For V1 your subscription is mocked. The full Stripe checkout, invoices, and proration ship in the next release.
          </p>
        </div>

        <div className="col-span-12 cell-strong bg-white p-6">
          <div className="overline text-[var(--ink-soft)]">/ Billing history</div>
          <div className="font-display text-2xl mt-1 mb-4">Orders</div>
          {orders.length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)]">No orders yet. <a href="/pricing" className="underline">Pick a plan</a>.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-soft)] border-b border-[var(--line)]">
                <tr className="text-left">
                  <th className="px-3 py-2 overline text-[var(--ink-soft)]">Order</th>
                  <th className="px-3 py-2 overline text-[var(--ink-soft)]">Plan</th>
                  <th className="px-3 py-2 overline text-[var(--ink-soft)]">Amount</th>
                  <th className="px-3 py-2 overline text-[var(--ink-soft)]">Status</th>
                  <th className="px-3 py-2 overline text-[var(--ink-soft)]">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-[var(--line)]" data-testid={`order-${o.order_id}`}>
                    <td className="px-3 py-3 font-mono-rc text-xs">{o.order_id}</td>
                    <td className="px-3 py-3">{o.plan.toUpperCase()} ({o.billing})</td>
                    <td className="px-3 py-3 font-display">${o.amount}</td>
                    <td className="px-3 py-3"><span className="tag tag-good"><CheckCircle size={12} weight="fill" /> {o.status}{o.mock ? " · mock" : ""}</span></td>
                    <td className="px-3 py-3 text-xs font-mono-rc">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="cell p-3">
      <div className="overline text-[var(--ink-soft)]">{label}</div>
      <div className="mt-1">{value || "—"}</div>
    </div>
  );
}
